/**
 * <collection-facets> — filter and sort on listing pages (collection and search results; the markup
 * is snippets/listing-facets.liquid).
 *
 * The markup is a plain GET form carrying Shopify's own filter parameters, so without JavaScript
 * the shopper picks values and presses Apply and the server returns a filtered page. With
 * JavaScript the same URL is fetched through the Section Rendering API, the grid is swapped in
 * place and the address bar is updated, so a filtered view stays shareable either way.
 */
class CollectionFacets extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('[data-facets-form]');
    this.sectionId = this.dataset.section;
    if (!this.form) return;

    // Apply buttons exist for the no-JavaScript path; here changes apply themselves.
    this.classList.add('facets--enhanced');
    this.addEventListener('click', this.onClick);
    this.addEventListener('selector:change', this.onSelectorChange);
    this.addEventListener('change', this.onPriceChange);
    this.addEventListener('keydown', this.onKeydown);
    document.addEventListener('click', this.onDocumentClick);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
  }

  get panels() {
    return [...this.querySelectorAll('[data-facet-panel]')];
  }

  onClick = (event) => {
    const toggle = event.target.closest('[data-facet-toggle]');
    if (!toggle) return;
    event.preventDefault();
    const name = toggle.dataset.facetToggle;
    const panel = this.querySelector(`[data-facet-panel="${name}"]`);
    const willOpen = panel?.hidden;
    this.closePanels();
    if (panel && willOpen) {
      panel.hidden = false;
      toggle.setAttribute('aria-expanded', 'true');
    }
  };

  onKeydown = (event) => {
    if (event.key !== 'Escape') return;
    // focus goes back to the control that opened the panel, not to the first one in the bar
    const open = this.querySelector('[data-facet-toggle][aria-expanded="true"]');
    this.closePanels();
    open?.focus();
  };

  onDocumentClick = (event) => {
    if (!this.contains(event.target)) this.closePanels();
  };

  closePanels() {
    this.panels.forEach((panel) => (panel.hidden = true));
    this.querySelectorAll('[data-facet-toggle]').forEach((t) => t.setAttribute('aria-expanded', 'false'));
  }

  /** Price is a pair of plain number inputs rather than a selector row. */
  onPriceChange = (event) => {
    if (!event.target.matches('[data-facet-price]')) return;
    this.onSelectorChange();
  };

  onSelectorChange = () => {
    clearTimeout(this.timer);
    this.timer = setTimeout(() => this.apply(), 250);
  };

  /** Build the filtered URL from the form and render it through the Section Rendering API. */
  async apply() {
    const params = new URLSearchParams(new FormData(this.form));
    for (const [key, value] of [...params]) {
      if (value === '') params.delete(key, value);
    }
    const query = params.toString();
    const url = query ? `${this.form.action}?${query}` : this.form.action;
    const section = document.querySelector(`[data-section-id="${this.sectionId}"]`);
    section?.classList.add('collection--loading');
    try {
      const response = await fetch(`${url}${query ? '&' : '?'}section_id=${this.sectionId}`, {
        cache: 'no-store',
      });
      const markup = await response.text();
      const parsed = new DOMParser().parseFromString(markup, 'text/html');
      const nextResults = parsed.querySelector('[data-collection-results]');
      const nextFacets = parsed.querySelector('[data-facets]');
      const results = document.querySelector('[data-collection-results]');
      if (nextResults && results) results.innerHTML = nextResults.innerHTML;
      if (nextFacets) this.refreshCounts(nextFacets);
      // Anything else on the page that states the filtered result (the search page's heading
      // count) opts in with data-facets-refresh and an id, and is refreshed from the same response.
      parsed.querySelectorAll('[data-facets-refresh][id]').forEach((next) => {
        const current = document.getElementById(next.id);
        if (current) current.innerHTML = next.innerHTML;
      });
      window.history.replaceState({}, '', url);
      document.dispatchEvent(new CustomEvent('collection:filtered', { detail: { url } }));
    } finally {
      section?.classList.remove('collection--loading');
    }
  }

  /**
   * Update which filter values are still available without rebuilding the controls, so the row
   * the shopper is using keeps its focus and its open state.
   */
  refreshCounts(nextFacets) {
    const nextItems = new Map(
      [...nextFacets.querySelectorAll('.selector__item')].map((item) => [
        `${item.querySelector('.selector__input')?.name}:${item.dataset.value}`,
        item,
      ]),
    );
    this.querySelectorAll('.selector__item').forEach((item) => {
      const input = item.querySelector('.selector__input');
      if (!input) return;
      const next = nextItems.get(`${input.name}:${item.dataset.value}`);
      const disabled = next?.querySelector('.selector__input')?.disabled ?? false;
      input.disabled = disabled && !input.checked;
      item.classList.toggle('selector__item--disabled', input.disabled);
    });
  }
}

if (!customElements.get('collection-facets')) customElements.define('collection-facets', CollectionFacets);
